import { jsx, jsxs } from "react/jsx-runtime";
import { useRef, useMemo, useEffect } from "react";
import { Shimmer } from "../../../ai-elements/shimmer.js";
import { VoiceWidgetInTextChat } from "../../components/VoiceWidgetInTextChat.js";
import { VoiceTranscriptBubble } from "./VoiceTranscriptBubble.js";
const VoiceScrollList = ({
  lines,
  widgetEvents,
  onLoginCompleted,
  onLoginFailed,
  pendingStatus
}) => {
  const scrollRef = useRef(null);
  const liveLoginCallbacks = useMemo(
    () => ({ onLoginCompleted, onLoginFailed }),
    [onLoginCompleted, onLoginFailed]
  );
  const items = useMemo(() => {
    const merged = [];
    for (const line of lines) {
      merged.push({ kind: "line", key: `line:${line.id}`, createdAt: line.createdAt, line });
    }
    let firstPromptLoginAt;
    let latestPromptLoginEvent;
    for (const event of widgetEvents) {
      if (event.type === "data-widget-prompt-login") {
        if (firstPromptLoginAt === void 0) firstPromptLoginAt = event.createdAt;
        latestPromptLoginEvent = event;
      }
    }
    if (latestPromptLoginEvent && firstPromptLoginAt !== void 0) {
      merged.push({
        kind: "widget",
        key: "widget:data-widget-prompt-login",
        createdAt: firstPromptLoginAt,
        event: latestPromptLoginEvent
      });
    }
    for (const event of widgetEvents) {
      if (event.type === "data-widget-prompt-login") continue;
      merged.push({ kind: "widget", key: `widget:${event.id}`, createdAt: event.createdAt, event });
    }
    merged.sort((a, b) => a.createdAt - b.createdAt);
    return merged;
  }, [lines, widgetEvents]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [items, pendingStatus]);
  const statusText = pendingStatus == null ? void 0 : pendingStatus.trim();
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref: scrollRef,
      style: {
        flex: "1 1 auto",
        minHeight: 0,
        width: "100%",
        overflowY: "auto",
        // decisions/078 follow-up — generous top padding so the first
        // transcript bubble doesn't crowd the top edge of the overlay.
        // Extra bottom space so the last rows can scroll under the dock fade.
        padding: "48px 18px 130px",
        background: "transparent",
        display: "flex",
        flexDirection: "column"
      },
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            width: "100%",
            maxWidth: 768,
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: 24
          },
          children: [
            items.length === 0 ? (
              // Suppress the idle placeholder while a status row is showing (e.g.
              // the "Connecting…" shimmer before the call goes live), so the
              // shimmer reads as the active indicator on its own.
              statusText ? null : /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    color: "var(--voice-header-fg, #9CA3AF)",
                    opacity: 0.5,
                    fontSize: 13,
                    fontStyle: "italic",
                    textAlign: "center",
                    paddingTop: 8
                  },
                  children: "Transcripts will appear here as you speak."
                }
              )
            ) : items.map(
              (item) => item.kind === "line" ? /* @__PURE__ */ jsx(VoiceTranscriptBubble, { line: item.line }, item.key) : /* @__PURE__ */ jsx("div", { style: { width: "100%" }, children: /* @__PURE__ */ jsx(
                VoiceWidgetInTextChat,
                {
                  type: item.event.type,
                  data: item.event.data,
                  liveLoginCallbacks,
                  collapseProductCardFeatures: true
                }
              ) }, item.key)
            ),
            statusText ? /* @__PURE__ */ jsx("div", { style: { width: "100%", padding: "4px 2px" }, children: /* @__PURE__ */ jsx(
              Shimmer,
              {
                as: "p",
                style: { fontSize: 15, lineHeight: "22px", margin: 0, fontFamily: "inherit" },
                color: "var(--voice-header-fg, #6B7280)",
                highlightColor: "#FFFFFF",
                children: statusText
              }
            ) }) : null
          ]
        }
      )
    }
  );
};
export {
  VoiceScrollList
};
//# sourceMappingURL=VoiceScrollList.js.map
